import React from "react";
import styled from "styled-components";
import { TextField, Typography, Tooltip } from "@mui/material";
import UndoIcon from "@mui/icons-material/Undo";
import RedoIcon from "@mui/icons-material/Redo";
import DeleteSweepIcon from "@mui/icons-material/DeleteSweep";
import CropSquareIcon from "@mui/icons-material/CropSquare";
import SaveIcon from "@mui/icons-material/Save";
import FolderOpenIcon from "@mui/icons-material/FolderOpen";
import { GuidedTour, TourStep } from "../../inputGraphEditor/components/GuidedTour";

export type DrawingTool = "boundary" | "entrance" | "fixedspace" | "corridor" | "pillars" | "text" | null;

interface TopToolbarProps {
    documentTitle: string;
    onDocumentTitleChange: (value: string) => void;
    gridSpacing: number;
    onGridSpacingChange: (value: number) => void;
    activeTool: DrawingTool;
    onToolChange: (tool: DrawingTool) => void;
    onFinalize?: () => void;
    onClear: () => void;
    drawing: boolean;
    onSelectDrawing: () => void;
    onSelectMove: () => void;
    deleteWallMode: boolean;
    onToggleDeleteWallMode: () => void;
    onUndo: () => void;
    onRedo: () => void;
    canUndo: boolean;
    canRedo: boolean;
    canUseFixedRoom: boolean;
    onSave?: () => void;
    isSaving?: boolean;
    canSave?: boolean;
    onViewSavedDocuments?: () => void;
    showViewSavedDocuments?: boolean;
    tutorialSteps?: TourStep[];
    tutorialId?: string;
    tutorialAutoStart?: boolean;
}

/* ── Layout ── */

const ToolbarContainer = styled.div`
    display: flex;
    align-items: center;
    gap: 6px;
    padding: 10px 16px;
    background: transparent;
    font-family: Poppins, sans-serif;
`;

const ToolGroup = styled.div`
    display: flex;
    align-items: center;
    gap: 6px;
`;

const Divider = styled.div`
    width: 1px;
    height: 28px;
    background: rgba(26, 26, 26, 0.2);
    margin: 0 6px;
    flex-shrink: 0;
`;

const Spacer = styled.div`
    flex: 1;
    min-width: 8px;
`;

/* ── File Name ── */

const TitleLabel = styled(Typography)`
    && {
        font-family: Poppins, sans-serif;
        font-size: 12px;
        font-weight: 700;
        color: #1a1a1a;
        white-space: nowrap;
        letter-spacing: 0.02em;
        text-transform: uppercase;
    }
`;

const TitleInput = styled(TextField)`
    min-width: 150px;
    max-width: 180px;

    .MuiOutlinedInput-root {
        height: 36px;
        background: #fff8ef;
        border-radius: 8px;
        font-family: Poppins, sans-serif;
        font-weight: 600;
        font-size: 13px;

        fieldset {
            border: 1.5px solid rgba(26, 26, 26, 0.25);
        }

        &:hover fieldset {
            border-color: rgba(26, 26, 26, 0.5);
        }

        &.Mui-focused fieldset {
            border-color: #FF9A56;
            border-width: 2px;
        }

        input {
            padding: 8px 12px;
            color: #1a1a1a;
        }
    }
`;

/* ── Tool Buttons (canvas mode + tools) ── */

const ToolButton = styled.button<{ $active?: boolean }>`
    padding: 6px 14px;
    height: 36px;
    border-radius: 8px;
    border: 1.5px solid ${(props) => (props.$active ? "#1a1a1a" : "rgba(26, 26, 26, 0.25)")};
    background: ${(props) => (props.$active ? "#FF9A56" : "transparent")};
    color: #1a1a1a;
    font-family: Poppins, sans-serif;
    font-size: 13px;
    font-weight: ${(props) => (props.$active ? 700 : 500)};
    cursor: pointer;
    transition: all 0.15s ease;
    display: inline-flex;
    align-items: center;
    gap: 5px;
    white-space: nowrap;

    &:hover {
        background: ${(props) => (props.$active ? "#FFB074" : "rgba(251, 229, 198, 0.6)")};
        border-color: rgba(26, 26, 26, 0.5);
    }

    &:active {
        transform: scale(0.97);
    }

    &:disabled {
        opacity: 0.35;
        cursor: not-allowed;
        transform: none;
    }
`;

/* ── Icon-only buttons (Undo / Redo) ── */

const IconButton = styled.button<{ $active?: boolean }>`
    width: 36px;
    height: 36px;
    border-radius: 8px;
    border: 1.5px solid rgba(26, 26, 26, 0.15);
    background: transparent;
    color: #1a1a1a;
    cursor: pointer;
    transition: all 0.15s ease;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    padding: 0;

    &:hover:not(:disabled) {
        background: rgba(251, 229, 198, 0.6);
        border-color: rgba(26, 26, 26, 0.35);
    }

    &:active:not(:disabled) {
        transform: scale(0.93);
    }

    &:disabled {
        opacity: 0.3;
        cursor: not-allowed;
    }
`;

/* ── Action Buttons ── */

const ActionButton = styled.button<{ $variant: "finalize" | "clear" | "save" | "secondary" | "tutorial" }>`
    padding: 6px 18px;
    height: 36px;
    border-radius: 8px;
    font-family: Poppins, sans-serif;
    font-size: 13px;
    font-weight: 700;
    cursor: pointer;
    transition: all 0.15s ease;
    display: inline-flex;
    align-items: center;
    gap: 5px;
    white-space: nowrap;

    ${(props) => {
        switch (props.$variant) {
            case "finalize":
                return `
                    background: #4CAF50;
                    color: #fff;
                    border: 1.5px solid #388E3C;
                    &:hover { background: #43A047; }
                `;
            case "save":
                return `
                    background: #FF9A56;
                    color: #1a1a1a;
                    border: 1.5px solid #E68A3E;
                    &:hover { background: #FFB074; }
                `;
            case "secondary":
                return `
                    background: transparent;
                    color: #1a1a1a;
                    border: 1.5px solid rgba(26, 26, 26, 0.3);
                    &:hover { background: rgba(140, 184, 229, 0.2); border-color: rgba(26, 26, 26, 0.5); }
                `;
            case "tutorial":
                return `
                    background: #FFFFFF;
                    color: #1C4C82;
                    border: 2px solid #1C4C82;
                    &:hover { background: #1C4C82; color: #FFFFFF; }
                `;
            case "clear":
            default:
                return `
                    background: transparent;
                    color: #C62828;
                    border: 1.5px solid rgba(198, 40, 40, 0.35);
                    &:hover { background: rgba(239, 154, 154, 0.25); border-color: #C62828; }
                `;
        }
    }}

    &:active {
        transform: scale(0.97);
    }

    &:disabled {
        opacity: 0.4;
        cursor: not-allowed;
        transform: none;
    }
`;

export const TopToolbar: React.FC<TopToolbarProps> = ({
    documentTitle,
    onDocumentTitleChange,
    gridSpacing,
    onGridSpacingChange,
    activeTool,
    onToolChange,
    onFinalize,
    onClear,
    drawing,
    onSelectDrawing,
    onSelectMove,
    deleteWallMode,
    onToggleDeleteWallMode,
    onUndo,
    onRedo,
    canUndo,
    canRedo,
    canUseFixedRoom,
    onSave,
    isSaving,
    canSave,
    onViewSavedDocuments,
    showViewSavedDocuments,
    tutorialSteps,
    tutorialId,
    tutorialAutoStart = false,
}) => {
    const resolvedTutorialSteps = tutorialSteps ?? [];
    const tutorialTrigger =
        tutorialId && resolvedTutorialSteps.length > 0 ? (
            <GuidedTour
                steps={resolvedTutorialSteps}
                tourId={tutorialId}
                autoStart={tutorialAutoStart}
                renderTrigger={(startTour) => (
                    <ActionButton
                        $variant="tutorial"
                        onClick={startTour}
                        title="Start tutorial"
                    >
                        <span aria-hidden="true">💡</span>
                        Tutorial
                    </ActionButton>
                )}
            />
        ) : null;

    return (
        <ToolbarContainer>
            {/* ── File Name ── */}
            <ToolGroup>
                <TitleLabel>File</TitleLabel>
                <TitleInput
                    size="small"
                    value={documentTitle}
                    onChange={(event) => onDocumentTitleChange(event.target.value)}
                    placeholder="Untitled Document"
                    inputProps={{ "aria-label": "File name" }}
                />
            </ToolGroup>

            <Divider />

            {/* ── Canvas Mode (mutually exclusive) ── */}
            <ToolGroup>
                <ToolButton
                    $active={drawing}
                    onClick={onSelectDrawing}
                    title="Draw boundary lines"
                >
                    ✏️ Draw
                </ToolButton>
                <ToolButton
                    $active={!drawing && !deleteWallMode && activeTool !== "fixedspace"}
                    onClick={onSelectMove}
                    title="Pan and move the canvas"
                >
                    ✋ Move
                </ToolButton>
            </ToolGroup>

            <Divider />

            {/* ── Tools ── */}
            <ToolGroup>
                <ToolButton
                    $active={activeTool === "fixedspace"}
                    onClick={() => onToolChange(activeTool === "fixedspace" ? null : "fixedspace")}
                    title={canUseFixedRoom ? "Draw a fixed room rectangle" : "Draw a valid closed boundary first"}
                    disabled={!canUseFixedRoom}
                >
                    <CropSquareIcon sx={{ fontSize: 15 }} />
                    Fixed Room
                </ToolButton>
                <ToolButton
                    $active={deleteWallMode}
                    onClick={onToggleDeleteWallMode}
                    title="Delete wall tool (or double-click a wall to delete it, or Alt+Click it to delete instantly)"
                >
                    <DeleteSweepIcon sx={{ fontSize: 15 }} />
                    Delete Wall
                </ToolButton>
            </ToolGroup>

            <Divider />

            {/* ── History (icon-only) ── */}
            <ToolGroup>
                <Tooltip title="Undo" arrow>
                    <span>
                        <IconButton
                            onClick={onUndo}
                            disabled={!canUndo}
                            aria-label="Undo"
                        >
                            <UndoIcon sx={{ fontSize: 18 }} />
                        </IconButton>
                    </span>
                </Tooltip>
                <Tooltip title="Redo" arrow>
                    <span>
                        <IconButton
                            onClick={onRedo}
                            disabled={!canRedo}
                            aria-label="Redo"
                        >
                            <RedoIcon sx={{ fontSize: 18 }} />
                        </IconButton>
                    </span>
                </Tooltip>
            </ToolGroup>

            {/* ── Push actions to the right ── */}
            <Spacer />

            {/* ── Actions (right-aligned) ── */}
            <ToolGroup>
                {showViewSavedDocuments && onViewSavedDocuments && (
                    <ActionButton
                        $variant="secondary"
                        onClick={onViewSavedDocuments}
                        title="View saved documents"
                    >
                        <FolderOpenIcon sx={{ fontSize: 16 }} />
                        Saved Docs
                    </ActionButton>
                )}

                {onSave && (
                    <ActionButton
                        $variant="save"
                        onClick={onSave}
                        disabled={isSaving || !canSave}
                        title={canSave ? "Save document" : "Save requires closed boundary, named rooms/fixed rooms, or floorplans"}
                    >
                        <SaveIcon sx={{ fontSize: 16 }} />
                        {isSaving ? "Saving…" : "Save"}
                    </ActionButton>
                )}

                {onFinalize && (
                    <ActionButton
                        data-tour="new-input-finalize"
                        $variant="finalize"
                        onClick={onFinalize}
                        title="Finalize boundary"
                    >
                        Finalize
                    </ActionButton>
                )}

                <ActionButton data-tour="new-input-clear" $variant="clear" onClick={onClear}>
                    Clear
                </ActionButton>
                {tutorialTrigger}
            </ToolGroup>
        </ToolbarContainer>
    );
};

export default TopToolbar;
